/**
 * Favorite Button Component
 * Heart icon button for favoriting studios and instructors
 */

import { Suspense, memo, useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import heartReaction from './assets/heart-reaction.json';
import { useHapticTarget } from '@/contexts/HapticsContext';
import { usePopAnimation } from '@/hooks/ui';
import { rgbTupleFromHex } from '@/lib/utils/color';
import { DotLottiePlayer } from '@/lib/lottie/dot-lottie-player';
import styles from './favorite-button.module.less';

interface FavoriteButtonProps {
  isFavorited: boolean;
  isLoading?: boolean;
  isMutating?: boolean;
  onToggle: () => void;
  size?: 'sm' | 'md' | 'lg';
}

export function recolorHeartReaction(hex: string): Record<string, unknown> {
  const color = rgbTupleFromHex(hex)
    .split(',')
    .map((channel) => Number(channel.trim()) / 255);
  const animation = globalThis.structuredClone(heartReaction) as Record<string, unknown>;

  const recolor = (value: unknown): void => {
    if (!value || typeof value !== 'object') return;
    const node = value as Record<string, unknown>;
    const colorProperty = node.c as { k?: unknown } | undefined;
    if ((node.ty === 'fl' || node.ty === 'st') && Array.isArray(colorProperty?.k)) {
      colorProperty.k.splice(0, 3, ...color);
    }
    Object.values(node).forEach(recolor);
  };

  recolor(animation);
  return animation;
}

export const FavoriteButton = memo<FavoriteButtonProps>(
  ({ isFavorited, isLoading = false, isMutating = false, onToggle, size = 'md' }) => {
    const hapticRef = useHapticTarget<HTMLButtonElement>();
    const [showReaction, setShowReaction] = useState(false);
    const [isFinishingReaction, setIsFinishingReaction] = useState(false);
    const [reactionData, setReactionData] = useState<Record<string, unknown> | null>(null);
    const [heartPopping, triggerHeartPop] = usePopAnimation();
    // Whether the in-flight toggle was an "add" - the reaction only plays for adds
    // that actually succeed, never for removes or rolled-back failures.
    const pendingAddRef = useRef(false);
    const prevMutatingRef = useRef(false);

    const startReaction = useCallback(() => {
      if (showReaction || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      // Pop plays on the heart even if the Lottie overlay silently fails to load (WASM).
      triggerHeartPop();
      const accent = window
        .getComputedStyle(document.documentElement)
        .getPropertyValue('--color-primary')
        .trim();
      setReactionData(recolorHeartReaction(accent || '#ff616d'));
      setIsFinishingReaction(false);
      setShowReaction(true);
    }, [showReaction, triggerHeartPop]);

    useEffect(() => {
      // Start the success reaction only once the mutation resolves (isMutating
      // falls) and the entity ended up favorited. A failed add rolls back to
      // isFavorited=false, so it never triggers the reaction.
      if (prevMutatingRef.current && !isMutating && pendingAddRef.current && isFavorited) {
        pendingAddRef.current = false;
        startReaction();
      }
      prevMutatingRef.current = isMutating;
    }, [isMutating, isFavorited, startReaction]);

    const handleClick = () => {
      if (isLoading || isMutating) return;
      pendingAddRef.current = !isFavorited;
      onToggle();
    };

    return (
      <button
        type="button"
        ref={hapticRef}
        onClick={handleClick}
        disabled={isLoading || isMutating}
        className={clsx(
          styles.favoriteButton,
          styles[size],
          isFavorited && (!showReaction || isFinishingReaction) && styles.favorited,
          (isLoading || (isMutating && !showReaction)) && styles.disabled
        )}
        aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill={isFavorited ? 'currentColor' : 'none'}
          stroke={isFavorited ? 'currentColor' : 'currentColor'}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={clsx(
            styles.heartIcon,
            showReaction && !isFinishingReaction && styles.heartIconHidden,
            heartPopping && styles.heartIconPopping
          )}
        >
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
        {showReaction && reactionData && (
          <Suspense fallback={null}>
            <DotLottiePlayer
              className={clsx(
                styles.heartReaction,
                isFinishingReaction && styles.heartReactionFinishing
              )}
              data={reactionData}
              autoplay
              loop={false}
              dotLottieRefCallback={(animation) => {
                animation?.addEventListener('complete', () => {
                  setIsFinishingReaction(true);
                  window.setTimeout(() => {
                    setShowReaction(false);
                    setIsFinishingReaction(false);
                  }, 200);
                });
                animation?.addEventListener('loadError', () => setShowReaction(false));
              }}
              aria-hidden="true"
            />
          </Suspense>
        )}
      </button>
    );
  }
);

FavoriteButton.displayName = 'FavoriteButton';
